import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, TrendingUp, TrendingDown, Activity, Target, AlertCircle, ChevronDown, Share2, Copy, BarChart3, Calendar, Shield, Info } from 'lucide-react';
import { toast } from 'sonner';
import { formatErrorMessage } from '../utils/errorHandler';
import { motion } from 'framer-motion';
import {
  calculateAdvancedMetrics,
  analyzePeriodicPerformance,
  detectOverfitting,
  generateEquityWithDrawdown,
  BACKTEST_DISCLAIMERS,
  getMetricColor
} from '../utils/backtestAnalysis';
import {
  ResponsiveContainer,
  ComposedChart,
  LineChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine
} from 'recharts';

const BacktestResultPage = () => {
  const { backtestId, shareToken } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSharing, setIsSharing] = useState(false);
  const [advancedMetrics, setAdvancedMetrics] = useState(null);
  const [periodicPerformance, setPeriodicPerformance] = useState(null);
  const [overfittingWarnings, setOverfittingWarnings] = useState([]);
  const [equityWithDrawdown, setEquityWithDrawdown] = useState([]);
  const isSharedView = !!shareToken;

  useEffect(() => {
    fetchBacktestResult();
  }, [backtestId, shareToken]);

  const fetchBacktestResult = async () => {
    try {
      let backtest;
      
      if (isSharedView) {
        // Fetch shared backtest (no auth needed)
        const response = await axios.get(`/shared/backtest/${shareToken}`);
        backtest = response.data;
      } else {
        // Fetch user's backtest
        const backtests = await axios.get('/backtests');
        backtest = backtests.data.find(b => b.id === backtestId);
      }
      
      if (!backtest) {
        toast.error(formatErrorMessage(null, 'Backtest sonucu bulunamadı'));
        if (!isSharedView) {
          navigate('/strategies');
        }
        return;
      }
      
      setResult(backtest);
      
      // Calculate advanced analytics
      const metrics = calculateAdvancedMetrics(backtest);
      setAdvancedMetrics(metrics);
      
      // Analyze periodic performance
      const periodic = analyzePeriodicPerformance(backtest.trades);
      setPeriodicPerformance(periodic);
      
      // Detect overfitting
      const warnings = detectOverfitting(backtest);
      setOverfittingWarnings(warnings);
      
      // Generate equity with drawdown
      if (backtest.equity_curve) {
        const equityDD = generateEquityWithDrawdown(backtest.equity_curve);
        setEquityWithDrawdown(equityDD);
      }
      
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Backtest sonucu yüklenemedi'));
      if (!isSharedView) {
        navigate('/strategies');
      }
    } finally {
      setLoading(false);
    }
  };

  const prepareChartData = () => {
    if (!result || !result.trades) return [];
    
    return result.trades.map((trade, idx) => ({
      index: idx + 1,
      price: trade.entry_price,
      signal: trade.signal_type,
      profit: trade.profit_loss_percent,
      date: new Date(trade.entry_time).toLocaleDateString('tr-TR')
    }));
  };

  const handleShare = async () => {
    try {
      setIsSharing(true);
      const response = await axios.post(`/backtests/${backtestId}/share`);
      const shareUrl = `${window.location.origin}/shared/backtest/${response.data.share_token}`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(shareUrl);
      
      toast.success('Paylaşım linki kopyalandı!', {
        description: 'Link panoya kopyalandı. İstediğiniz yere yapıştırabilirsiniz.'
      });
      
      // Update local result
      setResult(prev => ({
        ...prev,
        is_public: true,
        share_token: response.data.share_token
      }));
    } catch (error) {
      toast.error('Paylaşım linki oluşturulamadı', {
        description: error.response?.data?.detail || 'Bir hata oluştu'
      });
    } finally {
      setIsSharing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark py-12">
        <div className="container mx-auto px-6 max-w-7xl">
          {/* Skeleton Header */}
          <div className="mb-8 animate-pulse">
            <div className="h-10 bg-slate-800 rounded w-64 mb-4"></div>
            <div className="h-6 bg-slate-800 rounded w-96"></div>
          </div>

          {/* Skeleton Hero Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="stat-card border-2 border-slate-800 bg-slate-900/50 p-6">
                  <div className="h-4 bg-slate-800 rounded w-24 mb-4"></div>
                  <div className="h-12 bg-slate-800 rounded w-32 mb-2"></div>
                  <div className="h-3 bg-slate-800 rounded w-28"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Skeleton Quick Insights */}
          <div className="mb-8 animate-pulse">
            <div className="stat-card border-2 border-slate-800 bg-slate-900/50 p-6">
              <div className="h-6 bg-slate-800 rounded w-48 mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="p-4 rounded-lg bg-slate-800/50">
                    <div className="h-4 bg-slate-700 rounded w-32 mb-2"></div>
                    <div className="h-3 bg-slate-700 rounded w-full"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Skeleton Chart */}
          <div className="mb-8 animate-pulse">
            <div className="stat-card border-2 border-slate-800 bg-slate-900/50 p-6">
              <div className="h-6 bg-slate-800 rounded w-32 mb-4"></div>
              <div className="h-[500px] bg-slate-800 rounded"></div>
            </div>
          </div>

          {/* Loading Text */}
          <div className="text-center mt-8">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-slate-800/50 rounded-full">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-brand-green border-t-transparent"></div>
              <p className="text-slate-400">Backtest sonuçları yükleniyor...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!result) return null;

  const isPositive = result.total_return > 0;

  return (
    <div className="min-h-screen bg-brand-dark py-12" data-testid="backtest-result-page">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/strategies')}
          className="mb-6"
          data-testid="back-to-strategies"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Stratejilere Dön
        </Button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-2" data-testid="backtest-title">
                Backtest Sonuçları
              </h1>
              <p className="text-slate-400">
                {result.symbol} • {result.timeframe}
              </p>
            </div>
            
            {/* 🔹 1. Backtest Rozetleri */}
            {result.badges && result.badges.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="backtest-badges">
                {result.badges.map((badge, idx) => {
                  const colorClasses = {
                    green: 'bg-green-900/30 border-green-500/50 text-green-300',
                    yellow: 'bg-yellow-900/30 border-yellow-500/50 text-yellow-300',
                    blue: 'bg-blue-900/30 border-blue-500/50 text-blue-300',
                    purple: 'bg-purple-900/30 border-purple-500/50 text-purple-300',
                    teal: 'bg-teal-900/30 border-teal-500/50 text-teal-300'
                  };
                  
                  const icons = {
                    green: '🟢',
                    yellow: '🟡',
                    blue: '🔵',
                    purple: '🟣',
                    teal: '🔷'
                  };
                  
                  return (
                    <Badge
                      key={idx}
                      className={`px-3 py-1.5 border ${colorClasses[badge.color] || colorClasses.blue}`}
                      title={badge.description}
                    >
                      {icons[badge.color]} {badge.label}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Üst Aksiyon Butonları */}
        <div className="flex justify-end mb-6 gap-2">
          {!isSharedView && (
            <Button
              variant="outline"
              onClick={handleShare}
              disabled={isSharing}
              className="flex items-center space-x-2"
              data-testid="share-backtest-btn"
            >
              {result.is_public ? (
                <>
                  <Copy className="h-4 w-4" />
                  <span>Linki Tekrar Kopyala</span>
                </>
              ) : (
                <>
                  <Share2 className="h-4 w-4" />
                  <span>{isSharing ? 'Paylaşılıyor...' : 'Paylaş'}</span>
                </>
              )}
            </Button>
          )}
          {!isSharedView && (
            <>
              <Button
                variant="outline"
                onClick={() => navigate(`/backtest/${result.strategy_id}`)}
                data-testid="change-params-btn"
              >
                🔄 Parametre Değiştir
              </Button>
              <Link to={`/strategies`}>
                <Button variant="outline" data-testid="view-strategy-btn">
                  🔗 Stratejiyi Gör
                </Button>
              </Link>
            </>
          )}
          {isSharedView && (
            <div className="text-sm text-slate-400 flex items-center space-x-2">
              <Share2 className="h-4 w-4" />
              <span>Paylaşılan Backtest</span>
            </div>
          )}
        </div>

        {/* 🟢 BÖLÜM 1: HERO METRICS - İLK BAKIŞTA KARAR */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
          data-testid="summary-metrics"
        >
          {/* Toplam Getiri - HERO CARD */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Card className={`stat-card border-2 relative overflow-hidden ${isPositive ? 'border-green-500/50 bg-gradient-to-br from-green-900/20 to-transparent' : 'border-red-500/50 bg-gradient-to-br from-red-900/20 to-transparent'}`}>
              <div className={`absolute top-0 right-0 w-32 h-32 ${isPositive ? 'bg-green-500/10' : 'bg-red-500/10'} rounded-full blur-3xl`}></div>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm text-slate-400">Toplam Getiri</CardTitle>
                  {isPositive ? <TrendingUp className="h-5 w-5 text-green-500" /> : <TrendingDown className="h-5 w-5 text-red-500" />}
                </div>
              </CardHeader>
              <CardContent>
                <div className={`text-5xl font-bold font-mono mb-1 ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                  {isPositive ? '+' : ''}{result.total_return.toFixed(2)}%
                </div>
                <div className="text-sm text-slate-400 mt-3 flex items-center justify-between">
                  <span>${result.initial_capital.toLocaleString()}</span>
                  <span className="text-slate-600">→</span>
                  <span className={`font-semibold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                    ${result.final_capital.toLocaleString()}
                  </span>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Win Rate - HERO CARD */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Card className="stat-card border-2 border-blue-500/30 bg-gradient-to-br from-blue-900/20 to-transparent relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl"></div>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm text-slate-400">Kazanma Oranı</CardTitle>
                  <Target className="h-5 w-5 text-blue-400" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-5xl font-bold font-mono text-blue-400 mb-1">
                  {result.win_rate.toFixed(1)}%
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs">
                    <div className="text-green-400 font-semibold">{result.winning_trades} Kazanan</div>
                  </div>
                  <div className="text-xs">
                    <div className="text-red-400 font-semibold">{result.losing_trades} Kaybeden</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Max Drawdown - HERO CARD */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Card className="stat-card border-2 border-red-500/30 bg-gradient-to-br from-red-900/20 to-transparent relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-full blur-3xl"></div>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm text-slate-400">Max Drawdown</CardTitle>
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-5xl font-bold font-mono text-red-400 mb-1">
                  -{result.max_drawdown.toFixed(2)}%
                </div>
                <div className="text-xs text-slate-500 mt-3">
                  En büyük sermaye düşüşü
                </div>
                {result.max_drawdown > 20 && (
                  <div className="mt-2 text-xs text-yellow-400 flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    Yüksek risk
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Toplam İşlem - HERO CARD */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Card className="stat-card border-2 border-purple-500/30 bg-gradient-to-br from-purple-900/20 to-transparent relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl"></div>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm text-slate-400">Toplam İşlem</CardTitle>
                  <BarChart3 className="h-5 w-5 text-purple-400" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-5xl font-bold font-mono text-purple-400 mb-1">
                  {result.total_trades}
                </div>
                <div className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(result.start_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} - {new Date(result.end_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {/* QUICK INSIGHTS - AI Style Analysis */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="mb-8"
        >
          <Card className="border-border/40 bg-gradient-to-br from-indigo-900/10 to-transparent">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5 text-indigo-400" />
                Hızlı İçgörüler
              </CardTitle>
              <CardDescription>
                Bu stratejiniz hakkında bilmeniz gerekenler
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Insight 1: Return Quality */}
                <div className="p-4 rounded-lg bg-slate-900/50 border border-border/30">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">{isPositive ? '✅' : '❌'}</div>
                    <div>
                      <div className="font-semibold text-sm mb-1">
                        {isPositive ? 'Karlı Strateji' : 'Zararlı Strateji'}
                      </div>
                      <div className="text-xs text-slate-400">
                        {isPositive 
                          ? `${result.total_return.toFixed(2)}% getiri ile pozitif performans` 
                          : `${result.total_return.toFixed(2)}% kayıp, stratejinizi gözden geçirin`
                        }
                      </div>
                    </div>
                  </div>
                </div>

                {/* Insight 2: Win Rate Quality */}
                <div className="p-4 rounded-lg bg-slate-900/50 border border-border/30">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">
                      {result.win_rate >= 60 ? '🎯' : result.win_rate >= 40 ? '⚡' : '⚠️'}
                    </div>
                    <div>
                      <div className="font-semibold text-sm mb-1">
                        {result.win_rate >= 60 ? 'Yüksek Başarı' : result.win_rate >= 40 ? 'Dengeli Başarı' : 'Düşük Başarı'}
                      </div>
                      <div className="text-xs text-slate-400">
                        {result.win_rate >= 60 
                          ? `%${result.win_rate.toFixed(1)} kazanma oranı mükemmel` 
                          : result.win_rate >= 40
                          ? `%${result.win_rate.toFixed(1)} kazanma oranı makul seviyede`
                          : `%${result.win_rate.toFixed(1)} kazanma oranı düşük, iyileştirin`
                        }
                      </div>
                    </div>
                  </div>
                </div>

                {/* Insight 3: Risk Analysis */}
                <div className="p-4 rounded-lg bg-slate-900/50 border border-border/30">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">
                      {result.max_drawdown <= 10 ? '🛡️' : result.max_drawdown <= 20 ? '⚡' : '🚨'}
                    </div>
                    <div>
                      <div className="font-semibold text-sm mb-1">
                        {result.max_drawdown <= 10 ? 'Düşük Risk' : result.max_drawdown <= 20 ? 'Orta Risk' : 'Yüksek Risk'}
                      </div>
                      <div className="text-xs text-slate-400">
                        {result.max_drawdown <= 10 
                          ? `%${result.max_drawdown.toFixed(1)} drawdown ile güvenli` 
                          : result.max_drawdown <= 20
                          ? `%${result.max_drawdown.toFixed(1)} drawdown kabul edilebilir`
                          : `%${result.max_drawdown.toFixed(1)} drawdown çok yüksek!`
                        }
                      </div>
                    </div>
                  </div>
                </div>

                {/* Insight 4: Trade Frequency */}
                <div className="p-4 rounded-lg bg-slate-900/50 border border-border/30">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">
                      {result.total_trades >= 20 ? '📊' : result.total_trades >= 10 ? '📈' : '⏳'}
                    </div>
                    <div>
                      <div className="font-semibold text-sm mb-1">
                        {result.total_trades >= 20 ? 'Aktif Strateji' : result.total_trades >= 10 ? 'Orta Aktivite' : 'Az Trade'}
                      </div>
                      <div className="text-xs text-slate-400">
                        {result.total_trades >= 20 
                          ? `${result.total_trades} trade ile yeterli veri` 
                          : result.total_trades >= 10
                          ? `${result.total_trades} trade, daha fazla test edin`
                          : `Sadece ${result.total_trades} trade, daha uzun test gerekli`
                        }
                      </div>
                    </div>
                  </div>
                </div>

                {/* Insight 5: Sharpe Ratio */}
                {result.sharpe_ratio && (
                  <div className="p-4 rounded-lg bg-slate-900/50 border border-border/30">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">
                        {result.sharpe_ratio >= 1.5 ? '⭐' : result.sharpe_ratio >= 1.0 ? '💫' : '📉'}
                      </div>
                      <div>
                        <div className="font-semibold text-sm mb-1">
                          {result.sharpe_ratio >= 1.5 ? 'Harika Oran' : result.sharpe_ratio >= 1.0 ? 'İyi Oran' : 'Zayıf Oran'}
                        </div>
                        <div className="text-xs text-slate-400">
                          Sharpe: {result.sharpe_ratio.toFixed(2)} - {result.sharpe_ratio >= 1.5 ? 'risk/getiri dengesi mükemmel' : result.sharpe_ratio >= 1.0 ? 'risk/getiri dengesi iyi' : 'risk çok yüksek'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Insight 6: Profit Factor */}
                {result.profit_factor && (
                  <div className="p-4 rounded-lg bg-slate-900/50 border border-border/30">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">
                        {result.profit_factor >= 2.0 ? '💰' : result.profit_factor >= 1.5 ? '💵' : '⚖️'}
                      </div>
                      <div>
                        <div className="font-semibold text-sm mb-1">
                          {result.profit_factor >= 2.0 ? 'Güçlü Kar' : result.profit_factor >= 1.5 ? 'İyi Kar' : 'Zayıf Kar'}
                        </div>
                        <div className="text-xs text-slate-400">
                          Profit Factor: {result.profit_factor.toFixed(2)} - {result.profit_factor >= 2.0 ? 'kazançlar zararlardan 2x fazla' : result.profit_factor >= 1.5 ? 'pozitif denge' : 'kazanç/zarar neredeyse eşit'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* OVERFITTING & RISK WARNINGS */}
        {overfittingWarnings && overfittingWarnings.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-8"
          >
            <Card className="border-yellow-500/40 bg-yellow-900/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center text-yellow-400">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  Risk Analizi & Uyarılar
                </CardTitle>
                <CardDescription>
                  Bu strateji ile ilgili önemli uyarılar ve öneriler
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {overfittingWarnings.map((warning, idx) => {
                  const severityColors = {
                    critical: 'border-red-500/50 bg-red-900/20',
                    high: 'border-orange-500/50 bg-orange-900/20',
                    medium: 'border-yellow-500/50 bg-yellow-900/20',
                  };
                  
                  return (
                    <div key={idx} className={`p-4 border rounded-lg ${severityColors[warning.severity]}`}>
                      <p className="font-medium text-sm mb-2">{warning.message}</p>
                      <p className="text-xs text-slate-400">
                        <strong>Öneri:</strong> {warning.recommendation}
                      </p>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* ADVANCED METRICS GRID */}
        {advancedMetrics && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-8"
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-primary" />
                  Gelişmiş Metrikler
                </CardTitle>
                <CardDescription>
                  Profesyonel risk ve performans analizi
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Profit Factor</p>
                    <p className={`text-2xl font-bold font-mono ${getMetricColor(parseFloat(advancedMetrics.profitFactor), 'profitFactor')}`}>
                      {advancedMetrics.profitFactor}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">İdeal: &gt; 2.0</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Risk/Reward</p>
                    <p className={`text-2xl font-bold font-mono ${getMetricColor(parseFloat(advancedMetrics.riskRewardRatio), 'profitFactor')}`}>
                      {advancedMetrics.riskRewardRatio}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">İdeal: &gt; 2.0</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Sharpe Ratio</p>
                    <p className={`text-2xl font-bold font-mono ${getMetricColor(parseFloat(advancedMetrics.sharpeRatio), 'sharpe')}`}>
                      {advancedMetrics.sharpeRatio}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">İdeal: &gt; 1.5</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Recovery Factor</p>
                    <p className={`text-2xl font-bold font-mono text-primary`}>
                      {advancedMetrics.recoveryFactor}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Kâr / Max DD</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Ort. Kazanç</p>
                    <p className="text-xl font-bold font-mono text-green-400">
                      +{advancedMetrics.avgWin}%
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Ort. Zarar</p>
                    <p className="text-xl font-bold font-mono text-red-400">
                      {advancedMetrics.avgLoss}%
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Max Ardışık Kazanç</p>
                    <p className="text-xl font-bold font-mono text-green-400">
                      {advancedMetrics.maxConsecutiveWins}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Max Ardışık Kayıp</p>
                    <p className="text-xl font-bold font-mono text-red-400">
                      {advancedMetrics.maxConsecutiveLosses}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* PERIODIC PERFORMANCE */}
        {periodicPerformance && periodicPerformance.monthly.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mb-8"
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-primary" />
                  Dönemsel Performans Analizi
                </CardTitle>
                <CardDescription>
                  Stratejinin hangi dönemlerde çalıştığını görün
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="monthly">
                  <TabsList>
                    <TabsTrigger value="monthly">Aylık</TabsTrigger>
                    <TabsTrigger value="quarterly">Çeyreklik</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="monthly" className="mt-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border/40">
                            <th className="text-left py-2 px-4 text-slate-400">Dönem</th>
                            <th className="text-right py-2 px-4 text-slate-400">İşlem</th>
                            <th className="text-right py-2 px-4 text-slate-400">Kazanan</th>
                            <th className="text-right py-2 px-4 text-slate-400">Win Rate</th>
                            <th className="text-right py-2 px-4 text-slate-400">Kâr/Zarar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {periodicPerformance.monthly.map((month, idx) => (
                            <tr key={idx} className="border-b border-border/20">
                              <td className="py-2 px-4">{month.period}</td>
                              <td className="text-right py-2 px-4">{month.trades}</td>
                              <td className="text-right py-2 px-4 text-green-400">{month.wins}</td>
                              <td className="text-right py-2 px-4">
                                <Badge variant={parseFloat(month.winRate) >= 50 ? 'default' : 'destructive'}>
                                  {month.winRate}%
                                </Badge>
                              </td>
                              <td className={`text-right py-2 px-4 font-mono ${parseFloat(month.profit) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {parseFloat(month.profit) > 0 ? '+' : ''}{month.profit}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="quarterly" className="mt-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border/40">
                            <th className="text-left py-2 px-4 text-slate-400">Çeyrek</th>
                            <th className="text-right py-2 px-4 text-slate-400">İşlem</th>
                            <th className="text-right py-2 px-4 text-slate-400">Kazanan</th>
                            <th className="text-right py-2 px-4 text-slate-400">Win Rate</th>
                            <th className="text-right py-2 px-4 text-slate-400">Kâr/Zarar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {periodicPerformance.quarterly.map((quarter, idx) => (
                            <tr key={idx} className="border-b border-border/20">
                              <td className="py-2 px-4">{quarter.period}</td>
                              <td className="text-right py-2 px-4">{quarter.trades}</td>
                              <td className="text-right py-2 px-4 text-green-400">{quarter.wins}</td>
                              <td className="text-right py-2 px-4">
                                <Badge variant={parseFloat(quarter.winRate) >= 50 ? 'default' : 'destructive'}>
                                  {quarter.winRate}%
                                </Badge>
                              </td>
                              <td className={`text-right py-2 px-4 font-mono ${parseFloat(quarter.profit) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {parseFloat(quarter.profit) > 0 ? '+' : ''}{quarter.profit}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* 2️⃣ TEST VARSAYIMLARI BİLGİ ÇUBUĞU */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-8"
        >
          <Card className="border-border/40 bg-blue-900/10" data-testid="test-assumptions">
            <CardContent className="py-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3 text-sm overflow-visible">
                <div>
                  <div className="text-slate-500 text-xs mb-1">Başlangıç Sermayesi</div>
                  <div className="font-bold">${result.test_assumptions?.initial_capital?.toLocaleString() || result.initial_capital.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-slate-500 text-xs mb-1">İşlem Tipi</div>
                  <div className="font-bold text-xs">{result.test_assumptions?.trade_type || 'Spot (Long)'}</div>
                </div>
                <div>
                  <div className="text-slate-500 text-xs mb-1">Pozisyon Sayısı</div>
                  <div className="font-bold">{result.test_assumptions?.max_open_positions || 1}</div>
                </div>
                <div>
                  <div className="text-slate-500 text-xs mb-1">Sinyal</div>
                  <div className="font-bold text-xs">{result.test_assumptions?.signal_generation || 'Mum kapanışı'}</div>
                </div>
                <div>
                  <div className="text-slate-500 text-xs mb-1">Emir</div>
                  <div className="font-bold text-xs">{result.test_assumptions?.order_simulation || 'Sonraki mum'}</div>
                </div>
                <div className="bg-yellow-900/10 p-2 rounded border border-yellow-500/20">
                  <div className="text-yellow-400 text-xs mb-1 font-semibold">💰 Komisyon</div>
                  <div className="font-bold text-yellow-300">{result.test_assumptions?.commission_rate || '0.1%'}</div>
                </div>
                <div className="bg-yellow-900/10 p-2 rounded border border-yellow-500/20">
                  <div className="text-yellow-400 text-xs mb-1 font-semibold">📉 Slippage</div>
                  <div className="font-bold text-yellow-300">{result.test_assumptions?.slippage || '0.05%'}</div>
                </div>
                <div>
                  <div className="text-slate-500 text-xs mb-1">Lookahead Bias</div>
                  <div className="font-bold text-green-400 text-xs">✓ YOK</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* 🟣 BÖLÜM 2: GRAFİK - SINYAL GÖRSELLEŞTİRME */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="border-border/40 mb-8" data-testid="signal-chart">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Activity className="h-5 w-5 text-primary" />
                  <span>Performans Grafikleri</span>
                </div>
              </CardTitle>
              <CardDescription>
                Sinyal noktaları ve sermaye eğrisi
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="signals" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="signals">Sinyal Grafiği</TabsTrigger>
                  <TabsTrigger value="equity">Sermaye Eğrisi</TabsTrigger>
                </TabsList>

                <TabsContent value="signals">
              <div className="h-[500px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={prepareChartData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                    <XAxis 
                      dataKey="index" 
                      stroke="#94A3B8"
                      label={{ value: 'İşlem #', position: 'insideBottom', offset: -5, fill: '#94A3B8' }}
                    />
                    <YAxis 
                      stroke="#94A3B8"
                      label={{ value: 'Fiyat', angle: -90, position: 'insideLeft', fill: '#94A3B8' }}
                    />
                    <Tooltip
                      contentStyle={{ 
                        backgroundColor: 'rgba(15, 23, 42, 0.95)', 
                        border: '1px solid #475569',
                        borderRadius: '8px',
                        padding: '12px'
                      }}
                      labelStyle={{ 
                        color: '#F1F5F9',
                        fontWeight: '600',
                        fontSize: '14px',
                        marginBottom: '4px'
                      }}
                      itemStyle={{
                        color: '#E2E8F0',
                        fontSize: '13px'
                      }}
                    />
                    <Legend />
                    
                    {/* Price line */}
                    <Line
                      type="monotone"
                      dataKey="price"
                      stroke="#2E90FA"
                      strokeWidth={2}
                      dot={false}
                      name="Fiyat"
                    />
                    
                    {/* Buy signals */}
                    <Scatter
                      dataKey="price"
                      fill="#00E599"
                      shape="triangle"
                      name="AL Sinyali"
                      data={prepareChartData().filter(d => d.signal === 'BUY')}
                    />
                    
                    {/* Sell signals */}
                    <Scatter
                      dataKey="price"
                      fill="#F04438"
                      shape="triangleDown"
                      name="SAT Sinyali"
                      data={prepareChartData().filter(d => d.signal === 'SELL')}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Legend Açıklaması */}
              <div className="flex items-center justify-center space-x-6 mt-4 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-brand-green rounded-full" />
                  <span className="text-slate-400">AL Sinyali</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-brand-red rounded-full" />
                  <span className="text-slate-400">SAT Sinyali</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-0.5 bg-brand-blue" />
                  <span className="text-slate-400">Fiyat</span>
                </div>
              </div>
            </TabsContent>

            {/* 1️⃣ EQUITY CURVE TAB */}
            <TabsContent value="equity">
              <div className="h-[500px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={(result.equity_curve || []).map((point, idx) => {
                    return {
                      ...point,
                      index: idx + 1,
                      displayTime: point.timestamp ? new Date(point.timestamp).toLocaleDateString('tr-TR') : `İşlem ${idx + 1}`,
                    };
                  })}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                    <XAxis 
                      dataKey="index" 
                      stroke="#94A3B8"
                      label={{ value: 'İşlem #', position: 'insideBottom', offset: -5, fill: '#94A3B8' }}
                    />
                    <YAxis 
                      stroke="#94A3B8"
                      domain={['dataMin - 500', 'dataMax + 500']}
                      label={{ value: 'Sermaye ($)', angle: -90, position: 'insideLeft', fill: '#94A3B8' }}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`}
                    />
                    <Tooltip
                      contentStyle={{ 
                        backgroundColor: 'rgba(15, 23, 42, 0.95)', 
                        border: '1px solid #475569',
                        borderRadius: '8px',
                        padding: '12px'
                      }}
                      labelStyle={{ 
                        color: '#F1F5F9',
                        fontWeight: '600',
                        fontSize: '14px',
                        marginBottom: '4px'
                      }}
                      itemStyle={{
                        color: '#E2E8F0',
                        fontSize: '13px'
                      }}
                      formatter={(value, name) => {
                        if (name === 'Sermaye') {
                          return ['$' + parseFloat(value).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'Sermaye'];
                        }
                        return [value, name];
                      }}
                      labelFormatter={(label, payload) => {
                        if (payload && payload[0] && payload[0].payload.displayTime) {
                          return payload[0].payload.displayTime;
                        }
                        return `İşlem #${label}`;
                      }}
                    />
                    <Legend />
                    
                    {/* Başlangıç sermayesi referans çizgisi */}
                    <ReferenceLine
                      y={result.initial_capital}
                      stroke="#F04438"
                      strokeDasharray="3 3"
                      label={{ 
                        value: `Başlangıç: $${result.initial_capital.toLocaleString()}`, 
                        position: 'insideTopLeft', 
                        fill: '#F04438',
                        fontSize: 12
                      }}
                    />
                    
                    {/* Equity curve */}
                    <Line
                      type="monotone"
                      dataKey="capital"
                      stroke="#00E599"
                      strokeWidth={2}
                      dot={false}
                      name="Sermaye"
                      animationDuration={1500}
                    />
                    
                    {/* Buy signal markers on equity curve */}
                    <Scatter
                      dataKey="capital"
                      fill="#00E599"
                      shape="triangle"
                      name="AL Sinyali"
                      data={(() => {
                        // Create markers for BUY signals (trade entries)
                        const markers = [];
                        const equityCurve = result.equity_curve || [];
                        
                        // Pre-process equity curve with timestamps for faster lookup
                        const equityWithTime = equityCurve.map((point, idx) => ({
                          ...point,
                          index: idx + 1,
                          timestamp_ms: new Date(point.timestamp).getTime()
                        }));
                        
                        (result.trades || []).forEach(trade => {
                          if (trade.signal_type === 'BUY') {
                            const entryTime = new Date(trade.entry_time).getTime();
                            
                            // Find closest equity point to this trade's entry time
                            let closestPoint = null;
                            let minDiff = Infinity;
                            
                            equityWithTime.forEach((point) => {
                              const diff = Math.abs(point.timestamp_ms - entryTime);
                              
                              if (diff < minDiff) {
                                minDiff = diff;
                                closestPoint = point;
                              }
                            });
                            
                            if (closestPoint) {
                              markers.push({
                                capital: closestPoint.capital,
                                index: closestPoint.index,
                                timestamp: closestPoint.timestamp
                              });
                            }
                          }
                        });
                        
                        return markers;
                      })()}
                    />
                    
                    {/* Sell signal markers on equity curve */}
                    <Scatter
                      dataKey="capital"
                      fill="#F04438"
                      shape="triangleDown"
                      name="SAT Sinyali"
                      data={(() => {
                        // Create markers for SELL signals (trade exits)
                        const markers = [];
                        const equityCurve = result.equity_curve || [];
                        
                        // Pre-process equity curve with timestamps for faster lookup
                        const equityWithTime = equityCurve.map((point, idx) => ({
                          ...point,
                          index: idx + 1,
                          timestamp_ms: new Date(point.timestamp).getTime()
                        }));
                        
                        (result.trades || []).forEach(trade => {
                          const exitTime = new Date(trade.exit_time).getTime();
                          
                          // Find closest equity point to this trade's exit time
                          let closestPoint = null;
                          let minDiff = Infinity;
                          
                          equityWithTime.forEach((point) => {
                            const diff = Math.abs(point.timestamp_ms - exitTime);
                            
                            if (diff < minDiff) {
                              minDiff = diff;
                              closestPoint = point;
                            }
                          });
                          
                          if (closestPoint) {
                            markers.push({
                              capital: closestPoint.capital,
                              index: closestPoint.index,
                              timestamp: closestPoint.timestamp
                            });
                          }
                        });
                        
                        return markers;
                      })()}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-4 text-center text-sm text-slate-400">
                <p>Sermaye eğrisi, zaman içinde toplam sermayenizin nasıl değiştiğini gösterir.</p>
                <p className="text-xs mt-2">
                  <span className="text-green-400">↗ Yukarı trend</span> = Karlı dönem | 
                  <span className="text-red-400 ml-2">↘ Aşağı trend</span> = Zararlı dönem
                </p>
              </div>
            </TabsContent>
          </Tabs>
            </CardContent>
          </Card>
        </motion.div>

        {/* 🔵 BÖLÜM 3: DETAYLAR - KATLANABİLİR */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Accordion type="single" collapsible className="w-full" data-testid="details-accordion">
            <AccordionItem value="trades" className="border border-border/40 rounded-md mb-4 bg-card">
              <AccordionTrigger className="px-6 hover:no-underline">
                <div className="flex items-center space-x-3">
                  <Target className="h-5 w-5 text-primary" />
                  <span className="text-lg font-semibold">İşlem Detayları</span>
                  <Badge variant="outline">{result.trades.length} işlem</Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <Separator className="mb-4" />
                
                {/* 5️⃣ KAZANAN/KAYBEDEN SERİ METRİKLERİ + En İyi/Kötü İşlem */}
                {result.best_trade && result.worst_trade && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <Card className="bg-green-900/20 border-green-500/30">
                      <CardHeader>
                        <CardTitle className="text-sm flex items-center space-x-2">
                          <TrendingUp className="h-4 w-4 text-green-400" />
                          <span>En İyi İşlem</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-brand-green">
                          +{result.best_trade.profit_loss_percent.toFixed(2)}%
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          ${result.best_trade.profit_loss.toFixed(2)} kar
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-red-900/20 border-red-500/30">
                      <CardHeader>
                        <CardTitle className="text-sm flex items-center space-x-2">
                          <TrendingDown className="h-4 w-4 text-red-400" />
                          <span>En Kötü İşlem</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-brand-red">
                          {result.worst_trade.profit_loss_percent.toFixed(2)}%
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          ${result.worst_trade.profit_loss.toFixed(2)} zarar
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-blue-900/20 border-blue-500/30">
                      <CardHeader>
                        <CardTitle className="text-sm">En Uzun Kazanan Seri</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-brand-green">
                          {result.longest_winning_streak || 0}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          ardışık kazanan işlem
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-orange-900/20 border-orange-500/30">
                      <CardHeader>
                        <CardTitle className="text-sm">En Uzun Kaybeden Seri</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-brand-red">
                          {result.longest_losing_streak || 0}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          ardışık kaybeden işlem
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Ortalama Kazanç/Zarar + Yeni Metrikler */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <Card className="bg-green-900/10 border-green-500/20">
                    <CardContent className="py-4">
                      <div className="text-sm text-slate-400 mb-1">Ortalama Kazanç</div>
                      <div className="text-xl font-bold text-brand-green">
                        +{result.avg_win?.toFixed(2) || 0}%
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-900/10 border-red-500/20">
                    <CardContent className="py-4">
                      <div className="text-sm text-slate-400 mb-1">Ortalama Zarar</div>
                      <div className="text-xl font-bold text-brand-red">
                        {result.avg_loss?.toFixed(2) || 0}%
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-orange-900/10 border-orange-500/20">
                    <CardContent className="py-4">
                      <div className="text-sm text-slate-400 mb-1">Toplam Komisyon</div>
                      <div className="text-xl font-bold text-orange-400">
                        ${result.total_commission?.toFixed(2) || 0}
                      </div>
                      {result.commission_impact_percent && (
                        <div className="text-xs text-slate-500 mt-1">
                          {result.commission_impact_percent.toFixed(2)}% etki
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-900/10 border-blue-500/20">
                    <CardContent className="py-4">
                      <div className="text-sm text-slate-400 mb-1">Ort. İşlem Süresi</div>
                      <div className="text-xl font-bold text-blue-400">
                        {result.avg_trade_duration_hours?.toFixed(1) || 0}h
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {result.avg_trade_duration_hours ? 
                          `${(result.avg_trade_duration_hours / 24).toFixed(1)} gün` : 
                          '-'
                        }
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* İşlem Tablosu */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/40">
                        <th className="text-left py-3 px-2 text-slate-400 font-semibold">#</th>
                        <th className="text-left py-3 px-2 text-slate-400 font-semibold">Tarih</th>
                        <th className="text-left py-3 px-2 text-slate-400 font-semibold">Giriş</th>
                        <th className="text-left py-3 px-2 text-slate-400 font-semibold">Çıkış</th>
                        <th className="text-left py-3 px-2 text-slate-400 font-semibold">Pozisyon</th>
                        <th className="text-left py-3 px-2 text-slate-400 font-semibold">Kar/Zarar</th>
                        <th className="text-left py-3 px-2 text-slate-400 font-semibold">%</th>
                        <th className="text-left py-3 px-2 text-slate-400 font-semibold">Komisyon</th>
                        <th className="text-left py-3 px-2 text-slate-400 font-semibold">Süre</th>
                        <th className="text-left py-3 px-2 text-slate-400 font-semibold">Çıkış Nedeni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.trades.slice(0, 20).map((trade, idx) => (
                        <tr key={idx} className="border-b border-border/20 hover:bg-card/50">
                          <td className="py-3 px-2 font-mono text-slate-500">#{idx + 1}</td>
                          <td className="py-3 px-2 text-slate-300">
                            {new Date(trade.entry_time).toLocaleDateString('tr-TR')}
                          </td>
                          <td className="py-3 px-2 font-mono">${trade.entry_price.toFixed(2)}</td>
                          <td className="py-3 px-2 font-mono">${trade.exit_price.toFixed(2)}</td>
                          <td className="py-3 px-2 font-mono text-slate-400">
                            {trade.position_size ? `$${trade.position_size.toFixed(2)}` : '-'}
                          </td>
                          <td className={`py-3 px-2 font-mono font-bold ${
                            trade.profit_loss >= 0 ? 'text-brand-green' : 'text-brand-red'
                          }`}>
                            ${trade.profit_loss.toFixed(2)}
                          </td>
                          <td className={`py-3 px-2 font-mono ${
                            trade.profit_loss_percent >= 0 ? 'text-brand-green' : 'text-brand-red'
                          }`}>
                            {trade.profit_loss_percent >= 0 ? '+' : ''}{trade.profit_loss_percent.toFixed(2)}%
                          </td>
                          <td className="py-3 px-2 font-mono text-orange-400">
                            {trade.commission ? `-$${trade.commission.toFixed(2)}` : '-'}
                          </td>
                          <td className="py-3 px-2 font-mono text-slate-400">
                            {trade.duration_hours ? `${trade.duration_hours.toFixed(1)}h` : '-'}
                          </td>
                          <td className="py-3 px-2 text-xs">
                            {trade.exit_reason ? (
                              <span className={`px-2 py-1 rounded ${
                                trade.exit_reason === 'Take Profit' ? 'bg-green-500/20 text-green-400' :
                                trade.exit_reason === 'Stop Loss' ? 'bg-red-500/20 text-red-400' :
                                trade.exit_reason === 'Signal' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-slate-500/20 text-slate-400'
                              }`}>
                                {trade.exit_reason}
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {result.trades.length > 20 && (
                    <div className="text-center py-4 text-sm text-slate-500">
                      İlk 20 işlem gösteriliyor. Toplam {result.trades.length} işlem.
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </motion.div>

        {/* Yasal Uyarı */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-8 p-6 bg-yellow-900/10 border border-yellow-500/30 rounded-md"
          data-testid="legal-disclaimer"
        >
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-2 text-sm text-slate-300">
              <p><strong className="text-yellow-400">Önemli Uyarı:</strong></p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Bu sonuçlar yatırım tavsiyesi değildir.</li>
                <li>Backtest geçmiş verilere dayalı simülasyondur.</li>
                <li>Gerçek piyasa sonuçları farklı olabilir.</li>
                <li>Platform hiçbir şekilde emir iletmez.</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* LOOKAHEAD BIAS & EXECUTION INFO */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-8"
        >
          <Card className="border-green-500/40 bg-green-900/10">
            <CardHeader>
              <CardTitle className="text-lg flex items-center text-green-400">
                <Shield className="h-5 w-5 mr-2" />
                Test Şeffaflığı & Güvenilirlik
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start space-x-3 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                <Info className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm text-green-300 mb-1">
                    {BACKTEST_DISCLAIMERS.lookahead}
                  </p>
                  <p className="text-xs text-slate-400">
                    Tüm işlem sinyalleri mum kapanışı beklenip, bir sonraki mumun açılış fiyatı ile gerçekleştirilmiştir.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <Info className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm text-blue-300 mb-1">
                    {BACKTEST_DISCLAIMERS.execution}
                  </p>
                  <p className="text-xs text-slate-400">
                    Gerçekçi emir simülasyonu için bir sonraki mumun açılış fiyatı kullanılmıştır.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm text-yellow-300 mb-1">
                    {BACKTEST_DISCLAIMERS.slippage}
                  </p>
                  <p className="text-xs text-slate-400">
                    Gerçek işlemlerde slippage ve komisyonlar getirinizi etkileyecektir.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* BOTTOM DISCLAIMER - ALWAYS VISIBLE */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="mt-8"
        >
          <Card className="border-red-500/40 bg-red-900/10">
            <CardContent className="py-6">
              <div className="flex items-start space-x-4">
                <AlertCircle className="h-6 w-6 text-red-400 flex-shrink-0 mt-1" />
                <div className="space-y-2">
                  <p className="text-red-300 font-medium">
                    {BACKTEST_DISCLAIMERS.main}
                  </p>
                  <p className="text-red-300/80 text-sm">
                    {BACKTEST_DISCLAIMERS.risk}
                  </p>
                  <p className="text-xs text-slate-400 mt-3">
                    Bu backtest sonuçları yalnızca eğitim amaçlıdır. Gerçek piyasa koşulları, 
                    likidite, slippage, komisyonlar ve duygusal faktörler sonuçları önemli ölçüde etkileyebilir. 
                    Herhangi bir yatırım kararı vermeden önce profesyonel bir finansal danışmana başvurunuz.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* CTA Butonları */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <Button
            onClick={() => navigate('/strategies')}
            className="btn-primary flex-1"
            data-testid="back-to-strategies-btn"
          >
            Stratejilere Dön
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/backtest/${result.strategy_id}`)}
            className="flex-1"
            data-testid="run-new-backtest"
          >
            Yeni Test Çalıştır
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BacktestResultPage;
